import React, { useEffect } from "react";
import { useTheme, Paper, Box, Toolbar, IconButton, Tooltip, Typography, CircularProgress } from "@mui/material";
import { tokens } from "../../../assets/theme";
import { useState } from "react";
import useTable from "../../../components/useTable";
import Controls from "../../../components/controls/Controls";
import Popup from "../../../components/Popup";
import { InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { useQueryClient } from "@tanstack/react-query";
import ViewInstruction from "./viewInstruction";
import InstructionForm from "./instructionForm";
import InstructionDeleteForm from "./instructionDeleteForm";
import { FormattedMessage, useIntl } from "react-intl";
import messages from "./messages";
import { svSE, enUS } from '@mui/x-data-grid/locales';
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import { DisableIfNoRole, HideIfNoRole } from "../../../auth/authUi";

const headCells = [
    { id: "title", label: <FormattedMessage {...messages.title} /> },
    { id: "createdBy", label: <FormattedMessage {...messages.createdBy} /> },
    { id: "created", label: <FormattedMessage {...messages.created} /> },
    { id: "activitiesCount", label: <FormattedMessage {...messages.activitiesCount} /> },
    { id: "type", label: <FormattedMessage {...messages.instructionType} /> },
    { id: "view", label: "", disableSorting: true },
];

function InstructionGrid(props) {
    const language = localStorage.getItem('language') || 'sv';
    const translations = language === "sv" ? svSE : enUS;
    const columnOrderLocalStorageName = 'instructions-columnOrder';
    const columnWidthsLocalStorageName = 'instructions-columnWidths';
    const { formatMessage } = useIntl();
    const apiRef = useGridApiRef();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const queryClient = useQueryClient();
    const [show, setShow] = useState(false);
    const [recordForEdit, setRecordForEdit] = useState(0);
    const [openPopup, setOpenPopup] = useState(false);
    const [openPopupCreate, setOpenPopupCreate] = useState(false);
    const [openPopupEdit, setOpenPopupEdit] = useState(false);
    const [openPopupDelete, setOpenPopupDelete] = useState(false);
    const [records, setRecords] = useState(props.allInstructionsQuery.data);
    const [filterFn, setFilterFn] = useState({
        fn: (items) => {
            return items;
        },
    });
    const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({});
    const [instructionTypes, setInstructionTypes] = useState([
        {
            id: "0",
            titleSv: "Inställning",
            titleEn: "Setup",
        },
        {
            id: "1",
            titleSv: "Aktivitet",
            titleEn: "Activity",
        },
        {
            id: "2",
            titleSv: "Tröskelvärde",
            titleEn: "Threshold",
        },
    ]);
    const [selectedRow, setSelectedRow] = useState(null);

    const callbackModal = () => {
        setShow(false);
    };

    
    // Load persisted column order, widths, and sort order on mount
    function initColumnsFromLocalStorage(columns) {
        let columnsToFix = columns;
        // Load column order
        const savedColumnOrder = localStorage.getItem(columnOrderLocalStorageName);
        if (savedColumnOrder) {
            const orderArray = JSON.parse(savedColumnOrder);
            columnsToFix = reorderColumns(orderArray, columnsToFix);
        }

        // Load column widths
        const savedWidths = localStorage.getItem(columnWidthsLocalStorageName);
        if (savedWidths) {
            const widthMap = JSON.parse(savedWidths);
            columnsToFix = applyColumnWidths(widthMap, columnsToFix);
        }
        return columnsToFix;
    };

    // Reorder columns based on saved order
    const reorderColumns = (orderArray, columnsToFix) => {
        const reorderedColumns = orderArray.map((field) =>
            columnsToFix.find((col) => col.field === field)
        );
        reorderedColumns.push(...columnsToFix.filter((col) => !reorderedColumns.includes(col)));
        return reorderedColumns;
    };

    // Apply saved column widths
    const applyColumnWidths = (widthMap, columnsToFix) => {
        const updatedColumns = columnsToFix.map((col) => ({
            ...col,
            width: widthMap[col.field] || col.width,
            flex: widthMap[col.field] !== undefined ? undefined : col.flex,
        }));
        return updatedColumns;
    };

    // Handle column order change
    const handleColumnOrderChange = () => {
        if (!apiRef.current) {
            return;
        }
        const orderedColumns = apiRef.current.getVisibleColumns();
        const newColumnOrder = orderedColumns.map((col) => col.field);
        localStorage.setItem(columnOrderLocalStorageName, JSON.stringify(newColumnOrder));
    };

    // Handle column resize
    const handleColumnResize = (params) => {
        const { colDef, width } = params;
        const newWidths = JSON.parse(localStorage.getItem(columnWidthsLocalStorageName)) || {};
        newWidths[colDef.field] = width;
        localStorage.setItem(columnWidthsLocalStorageName, JSON.stringify(newWidths));
    };
    
    const addOrEdit = (materialSource, resetForm) => {

        //updateMaterialSourceQuery(materialSource);
        resetForm();
        setRecordForEdit(null);
        setOpenPopup(false);
        setRecords(props.allInstructionsQuery.data);
    };

    useEffect(() => {

        setRecords(props.allInstructionsQuery.data);

    }, [props.allInstructionsQuery]);

    const handleSearch = (e) => {
        let target = e.target;
        setFilterFn({
            fn: (items) => {
                if (target.value === "") return items;
                else
                    return items.filter(
                        (x) =>
                            x.title.toLowerCase().includes(target.value) ||
                            x.createdBy.toLowerCase().includes(target.value)
                    );
            },
        });

        
    };

    const openInPopup = (instruction, type) => {
        //Update to selected instruction
        setRecordForEdit(instruction);
        if (type === "view") {
            setOpenPopup(true);
        } else if (type === "edit") {
            setOpenPopupEdit(true);
        } else if (type === "delete") {
            setOpenPopupDelete(true);
        }
    };

    const openInPopupCreate = () => {
        setRecordForEdit(0);
        setOpenPopupCreate(true);
    };

    const toggleShow = (params) => () => {
        setShow((p) => !p);
        setSelectedRow(params);
    };

    function getTypeTitle(id) {
        let type = instructionTypes.find((type) => type.id.toString() === id.toString());

        if (language === "sv") {
            return type ? type.titleSv : "Okänd";
        } else if (language === "en") {
            return type ? type.titleEn : "Unknown";
        }
    }

    let columns = [
            {
                field: "title",
                headerName: formatMessage(messages.title),
                flex: 1,
                minWidth: 100,
            },
            {
                field: "createdBy",
                headerName: formatMessage(messages.createdBy),
                flex: 1,
                minWidth: 100,
            },
            {
                field: "created",
                headerName: formatMessage(messages.created),
                flex: 1,
                minWidth: 100,
            },
            {
                field: "activityCount",
                headerName: formatMessage(messages.activitiesCount),
                flex: 1,
                minWidth: 100,
            },
            {
                field: "type",
                headerName: formatMessage(messages.instructionType),
                flex: 1,
                minWidth: 100,
                renderCell: (params) => {
                    return (
                        <div>
                            {getTypeTitle(params.row.type)}
                        </div>
                    )
                },
            },
            {
                field: "view",
                headerName: "",
                flex: 1,
                minWidth: 100,
                renderCell: ({ row }) => {
                    return (
                        <>
                            <Tooltip title={<Typography fontSize="12px"><FormattedMessage {...messages.updateInstruction} /></Typography>}>
                                <IconButton
                                    aria-label="Edit"
                                    size="small"
                                    sx={{
                                        "&:hover": {
                                            backgroundColor: colors.primary[500],
                                        },
                                        backgroundColor: colors.primary[700],
                                        margin: 1,
                                    }}
                                    onClick={() => {
                                        openInPopup(row, "edit");
                                    }}
                                >
                                    <EditIcon fontSize="inherit" />
                                </IconButton>
                            </Tooltip>
                            <HideIfNoRole anyOf={[1, 2]}>
                                <Tooltip title={<Typography fontSize="12px">{row.activityCount > 0 ? <FormattedMessage {...messages.deleteInstructionNotPossible} /> : <FormattedMessage {...messages.deleteInstruction} />}</Typography>}>
                                    <img src="../../../assets/dot_transparent.png" alt="" />
                                    <IconButton
                                        disabled={row.activityCount > 0}
                                        aria-label="Delete"
                                        size="small"
                                        sx={{
                                            "&:hover": {
                                                backgroundColor: colors.redAccent[500],
                                            },
                                            backgroundColor: colors.redAccent[700],
                                            margin: 1,
                                        }}
                                        onClick={() => { openInPopup(row, "delete") }}>
                                        <DeleteOutlineOutlinedIcon fontSize="inherit" />
                                    </IconButton>
                                </Tooltip>
                            </HideIfNoRole>
                        </>
                    )
                },
            },
        ];

    if (props.allInstructionsQuery.isLoading) {
        return (
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                }}
            >
                <CircularProgress color="secondary" />
            </div>
        );
    }
    
    columns = initColumnsFromLocalStorage(columns);
    const filteredRows = filterFn.fn(records);

    return (
        <Box >
            <Box
                m="0px 15px 15px 15px"
                height="69vh"
                sx={{
                    "& .MuiDataGrid-root": {
                        backgroundColor: colors.primary[400],
                        borderColor: `${colors.primary[700]} !important`,
                        borderRadius: "0px",
                        fontSize: "1.0rem",
                        border: "0 !important",
                    },
                    "& .MuiDataGrid-main": {
                        borderRadius: "0px",
                        border: "0 !important",
                        borderColor: `${colors.primary[700]} !important`,
                    },
                    "& .MuiDataGrid-cell": {
                        borderColor: `${colors.primary[700]} !important`,
                        borderWidth: "2px !important",
                    },
                    "& .name.column--cell": {
                        color: colors.greenAccent[300],
                    },
                    "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: colors.primary[400],
                        fontSize: '1.0rem',
                        borderColor: `${colors.primary[700]} !important`,
                        borderWidth: "2px"
                    },
                    "& .MuiDataGrid-virtualScroller": {
                        backgroundColor: colors.primary[400],
                    },
                    "& .MuiDataGrid-footerContainer": {
                        borderTop: "none",
                        backgroundColor: colors.primary[600],
                    },
                    "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                        color: `${colors.gray[100]} !important`,
                    },
                    "& .super-app-theme--11": {
                        bgcolor: `${colors.redAccent[500]} !important`,
                    },
                    "& .super-app-theme--undefined-stop": {
                        bgcolor: `${colors.redAccent[500]} !important`,
                    },
                    "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIconButton": {
                        opacity: 0,
                        transition: "opacity 0.2s"
                        },
                        
                    "& .MuiDataGrid-columnHeader:hover .MuiDataGrid-menuIconButton": {
                        opacity: 1
                    },
                }}
            >
                <Toolbar sx={{ backgroundColor: colors.primary[400] }}>
                    <Controls.Input
                        sx={{ width: "600px" }}
                        label={<FormattedMessage {...messages.search} />}
                        InputProps={{
                            size: "small",
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        onChange={handleSearch}
                    />
                    <HideIfNoRole anyOf={[1, 2]}>
                        <Controls.Button
                            text={<FormattedMessage {...messages.add} />}
                            variant="outlined"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => {
                                openInPopupCreate();
                            }}
                            sx={{
                                "&:hover": {
                                    backgroundColor: "#799c4a",
                                },
                                backgroundColor: "#799c4a",
                                color: "#ffffff",
                                position: "absolute",
                                right: "10px",
                            }}
                        />
                    </HideIfNoRole>
                </Toolbar>

                <DataGridPro
                    apiRef={apiRef}
                    onColumnResize={handleColumnResize}
                    onColumnOrderChange={handleColumnOrderChange}
                    rows={filteredRows}
                    getRowId={(row) => row.id}
                    columns={columns}
                    onColumnVisibilityModelChange={(newModel) =>
                        setColumnVisibilityModel(newModel)
                    }
                    initialState={props.InitialState}
                    localeText={translations.components.MuiDataGrid.defaultProps.localeText}
                    slotProps={{
                        panel: {
                        sx: {
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                backgroundColor: "#799c4a",
                                opacity: 0.3,
                            },
                            "& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb": {
                                backgroundColor: "#799c4a",
                            },
                            "& .MuiDataGrid-panelFooter .MuiButtonBase-root": {
                                color: colors.primary[200],
                                fontSize: "0.75rem",
                                "&:hover": {
                                    color: colors.primary[300],
                                },
                            },
                            "& .MuiFormLabel-root.Mui-focused": {
                                color: colors.primary[200],
                            },
                        },
                        }
                    }}
                />
                
            <Popup
                title={<FormattedMessage {...messages.viewInstruction} />}
                openPopup={openPopup}
                setOpenPopup={setOpenPopup}
                maxWidht="95%"
            >
                <ViewInstruction id={recordForEdit.id} />
            </Popup>
            <Popup
                title={<FormattedMessage {...messages.createInstruction} />}
                openPopup={openPopupCreate}
                setOpenPopup={setOpenPopupCreate}
            >
                <InstructionForm companyKey={props.companyKey} setOpenPopup={setOpenPopupCreate} data={props.allInstructionsQuery} mode="create" />
            </Popup>
            <Popup
                title={<FormattedMessage {...messages.updateInstruction} />}
                openPopup={openPopupEdit}
                setOpenPopup={setOpenPopupEdit}
            >
                <InstructionForm companyKey={props.companyKey} data={props.allInstructionsQuery} mode="update" instructionToUpdate={recordForEdit} setOpenPopup={setOpenPopupEdit} setRecordForEdit={setRecordForEdit} />
            </Popup>
            <Popup
                title={<FormattedMessage {...messages.deleteInstructionQ} />}
                openPopup={openPopupDelete}
                setOpenPopup={setOpenPopupDelete}
            >
                <InstructionDeleteForm companyKey={props.companyKey} instruction={recordForEdit} setOpenPopup={setOpenPopupDelete} data={props.allInstructionsQuery} />
            </Popup>
        </Box>
    </Box>
    );
}

export default InstructionGrid;
